import { commonService } from "../../../utils/commonService";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";

const LEVEL_ORDER = ["veryhigh", "high", "moderate", "low", "verylow"];

const LEVEL_META = {
    veryhigh: {
        label: "Very High",
        color: "#1b9e4b",
        className: "very-high",
        priority: "HIGH",
        action: "Maximize rate, close discounts, restrict LOS, block for group",
    },
    high: {
        label: "High",
        color: "#6dad2e",
        className: "high",
        priority: "HIGH",
        action: "Push rates, limit promotions, manage inventory",
    },
    moderate: {
        label: "Moderate",
        color: "#e5a819",
        className: "moderate",
        priority: "MEDIUM",
        action: "Hold rates, monitor pace, activate tactical promos",
    },
    low: {
        label: "Low",
        color: "#e07416",
        className: "low",
        priority: "MEDIUM",
        action: "Increase promotions, open all channels",
    },
    verylow: {
        label: "Very Low",
        color: "#d32f2f",
        className: "very-low",
        priority: "CRITICAL",
        action: "Aggressive pricing, open all, consider market value",
    },
};

const FALLBACK = {
    veryhigh: 0,
    high: 0,
    moderate: 0,
    low: 0,
    verylow: 0,
};

const toInt = (v, fallback = 0) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
};

const safeClamp = (value, min, max) => {
    if (max < min) return min;
    return Math.max(min, Math.min(max, value));
};

const normalizeLevels = (data) => {
    const veryhigh = safeClamp(toInt(data?.veryhigh, FALLBACK.veryhigh), 4, 99);
    const high = safeClamp(toInt(data?.high, FALLBACK.high), 3, veryhigh - 1);
    const moderate = safeClamp(
        toInt(data?.moderate, FALLBACK.moderate),
        2,
        high - 1,
    );
    const low = safeClamp(toInt(data?.low, FALLBACK.low), 1, moderate - 1);
    const verylow = safeClamp(toInt(data?.verylow, FALLBACK.verylow), 0, low - 1);
    return { veryhigh, high, moderate, low, verylow };
};

const DemandLevels = ({ data, onUpdate }) => {
    const values = useMemo(() => normalizeLevels(data), [data]);
    const debounceTimerRef = useRef(null);
    const [pendingUpdates, setPendingUpdates] = useState({});

    const ranges = useMemo(
        () => ({
            veryhigh: { min: values.veryhigh, max: 100 },
            high: { min: values.high, max: values.veryhigh - 1 },
            moderate: { min: values.moderate, max: values.high - 1 },
            low: { min: values.low, max: values.moderate - 1 },
            verylow: { min: values.verylow, max: values.low - 1 },
        }),
        [values],
    );

    const getSliderBounds = (key) => {
        const idx = LEVEL_ORDER.indexOf(key);
        const prevKey = idx > 0 ? LEVEL_ORDER[idx - 1] : null;
        const nextKey = idx < LEVEL_ORDER.length - 1 ? LEVEL_ORDER[idx + 1] : null;

        const prevValue = prevKey ? values[prevKey] : 100;
        const nextValue = nextKey ? values[nextKey] : 0;

        const max = prevKey ? prevValue - 1 : 99;
        const min = key === "verylow" ? 0 : nextValue + 1;
        return { min, max };
    };

    const handleLevelChange = (key, nextValue) => {
        const { min, max } = getSliderBounds(key);
        const clamped = safeClamp(toInt(nextValue, values[key]), min, max);

        // Update local state immediately for UI responsiveness
        setPendingUpdates((prev) => ({
            ...prev,
            [key]: clamped,
        }));

        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set a new timer to call API after 500ms of inactivity
        debounceTimerRef.current = setTimeout(() => {
            onUpdate?.(key, clamped);
        }, 500);
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const scaleSegments = useMemo(() => {
        const units = LEVEL_ORDER.map((key) => {
            const range = ranges[key];
            // Inclusive width so all buckets cover the full 0-100 scale.
            return { key, units: Math.max(1, range.max - range.min + 1) };
        });
        const totalUnits = units.reduce((sum, item) => sum + item.units, 0) || 1;
        return units.map((item) => ({
            key: item.key,
            width: (item.units / totalUnits) * 100,
        }));
    }, [ranges]);

    return (
        <div className="demand-levels-container mt-3">
            <Container fluid>
                <Row className="mb-3">
                    <Col lg={12}>
                        <div className="info-section mb-0">
                            <p className="info-text">
                                <b className="info-title">Demand Level Thresholds : </b>
                                All 5 levels configurable. Adjust to match your property.
                            </p>
                        </div>
                    </Col>
                </Row>

                {LEVEL_ORDER.map((key) => {
                    const meta = LEVEL_META[key];
                    const isFloor = key === "verylow";
                    const minVal = pendingUpdates[key] !== undefined ? pendingUpdates[key] : values[key];
                    const range = ranges[key];
                    const sliderBounds = getSliderBounds(key);
                    return (
                        <div
                            key={key}
                            className={`demand-level-card ${meta.className}`}
                            style={{ marginBottom: "10px" }}
                        >
                            <div className="demand-card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-2">
                                        <span
                                            className="demand-level-badge"
                                            style={{
                                                backgroundColor: meta.color,
                                                color: "white",
                                                padding: "5px",
                                                borderRadius: "5px",
                                                fontSize: "x-small",
                                            }}
                                        >
                                            {meta.label}
                                        </span>
                                        <span className="text-muted" style={{ fontSize: "12px" }}>
                                            Score {range.min} - {range.max}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span
                                            style={{
                                                fontSize: "11px",
                                                color: meta.color,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {meta.priority}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "24px",
                                                color: meta.color,
                                                fontWeight: 800,
                                            }}
                                        >
                                            {minVal}
                                        </span>
                                    </div>
                                </div>

                                {!isFloor ? (
                                    <div className="d-flex align-items-center gap-2">
                                        {/*<small className="text-muted ds-range-bound">*/}
                                        {/*  {sliderBounds.min}*/}
                                        {/*</small>*/}
                                        <Form.Range id={commonService.generateGlobalClickTrackerID("DemandLevels", "form-range-onchange", key)}
                                            className="ds-range-input dynamic-range"
                                            min={sliderBounds.min}
                                            max={sliderBounds.max}
                                            step={1}
                                            value={minVal}
                                            onChange={(e) => handleLevelChange(key, e.target.value)}
                                            style={{  "--range-color": meta.color }}
                                        />
                                        {/*<small className="text-muted ds-range-bound">*/}
                                        {/*  {sliderBounds.max}*/}
                                        {/*</small>*/}
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            height: 5,
                                            borderRadius: 3,
                                            background: `${meta.color}44`,
                                        }}
                                    />
                                )}

                                <div
                                    style={{ fontSize: "12px", color: "#666" }}
                                >
                                    <strong>RM Action:</strong> {meta.action}
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div className="demand-table-section" style={{ padding: "14px" }}>
                    <h5 className="section-title">Score Scale</h5>
                    <div
                        style={{
                            display: "flex",
                            height: "32px",
                            borderRadius: "6px",
                            overflow: "hidden",
                        }}
                    >
                        {scaleSegments.map((seg) => (
                            <div
                                key={seg.key}
                                style={{
                                    width: `${seg.width}%`,
                                    background: LEVEL_META[seg.key].color,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontSize: "10px",
                                    fontWeight: 700,
                                }}
                            >
                                {seg.width >= 10
                                    ? `${LEVEL_META[seg.key].label} ${ranges[seg.key].min}-${ranges[seg.key].max}`
                                    : ""}
                            </div>
                        ))}
                    </div>
                    <div
                        className="d-flex justify-content-between mt-1 text-muted"
                        style={{ fontSize: "11px" }}
                    >
                        <span>0</span>
                        <span>100</span>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default DemandLevels;
